import React, { useEffect, useState, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  Fab,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import FormInput from "../../../../components/Forms/FormInput";
import { enqueueSnackbar } from "notistack";
import styles from "../../../../assets/styles/events/createEvent.styles";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  ADMIN_EVENT_CREATE,
  ADMIN_SPONSOR_ADD_OR_UPDATE,
} from "../../../../data/constants/apiRoutes";
import { post } from "../../../../server";
import { useMutation, useQueryClient } from "react-query";
import { Close, Edit } from "@mui/icons-material";
import FormLabel from "../../../../components/Forms/FormLabel";
import { validator } from "../../../../utils/helpers/validator";
import { NewSponsorSchema } from "../../../../utils/schemas/EventSchema";
import LoadingButton from "../../../../components/Buttons/LoadingButton";
import awsFileUpload from "../../../../utils/helpers/awsFileUpload";
import { uploadImageType } from "../../../../data/constants/uploadImageTypes";
import _ from "lodash";
import CropImage from "../../../../components/Modals/CropImage";
import GetAddressModal from "../../../../components/Modals/GetAddressModal";
import queryNames from "../../../../data/constants/queryNames";

const newSponsorInitialState = {
  sponser_name: "",
  email: "",
  company_name: "",
  website: "",
  address: "",
  latitude: "",
  longitude: "",
  description: "",
  image: "",
};

const drawerStyles = {
  container: {
    position: "relative",
    width: 500,
    bgcolor: "#fff",
    borderTopLeftRadius: "10px",
    borderBottomLeftRadius: "10px",
    p: 2,
    "& h2": {
      fontSize: 20,
      fontWeight: 600,
      color: "#000",
      mt: 1,
      mb: 2,
      textAlign: "center",
      textTransform: "capitalize",
    },
  },
  eventImageContainer: {
    borderRadius: "4px",
    border: "1px dashed var(--light-background-color-2, #E2E3E4)",
    bgcolor: "#FBFCFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 2,
    p: 3,
    height: 200,
    "& button": {
      borderRadius: "4px",
      border: "1px solid #2B2A69",
      bgcolor: "#FFF",
      color: "#2B2A69",
      fontWeight: 500,
      textTransform: "capitalize",
      "&:hover": {
        bgcolor: "#FFF",
        border: "1px solid #2B2A69",
      },
    },
    "& label": {
      color: "#767D90",
      fontSize: 14,
    },
  },
  coverImageContainer: {
    borderRadius: "4px",
    border: "1px solid #eee",
    height: 200,
    width: "100%",
    objectFit: "cover",
  },
  button: {
    mt: 2,
    width: 100,
    bgcolor: "#2B2A69",
    color: "#fff",
    textTransform: "capitalize",
    "&:hover": {
      bgcolor: "#2B2A69",
    },
  },
  coverHover: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%,-50%)",
    gap: "10px",
    transition: "display 0.3s ease",
    "& button": {
      borderRadius: "4px",
      border: "1px solid #2B2A69",
      bgcolor: "#FFF",
      color: "#2B2A69",
      fontWeight: 500,
      textTransform: "capitalize",
      "&:hover": {
        bgcolor: "#FFF",
        border: "1px solid #2B2A69",
      },
    },
    "& label": {
      color: "#767D90",
      fontSize: 14,
    },
  },
};

const SponsorTab = ({
  sponsors,
  currentStep,
  setCurrentStep,
  eventForm,
  setEventForm,
  loading = false,
  onSponsorCreated,
  onSponsorUpdated,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedSponsors, setSelectedSponsors] = useState([]);
  const [errors, setErrors] = useState({});

  // Create/Edit Sponsor Modal State
  const [sponsorModal, setSponsorModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingSponsorId, setEditingSponsorId] = useState(null);
  const [sponsorForm, setSponsorForm] = useState(newSponsorInitialState);
  const [cropModal, setCropModal] = useState(false);
  const [addressModal, setAddressModal] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  const [isCoverHovered, setIsCoverHovered] = useState(false);

  const coverRef = useRef();
  const queryClient = useQueryClient();



  useEffect(() => {
    setSelectedSponsors( eventForm.members?.sponsors.map((sponsorobj) => sponsorobj.company_sponser_id) );
  }, [eventForm.members?.sponsors]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleCheckboxChange = (member, checked) => {
    if (checked) {
      setSelectedSponsors( selectedSponsors => [...selectedSponsors, member.id]);
    } else {
      setSelectedSponsors( selectedSponsors.filter( (sponsor) => sponsor !== member.id ));
    }
  };

  // Create/Edit Sponsor Modal Handlers
  const handleOpenCreateModal = () => {
    setIsEditMode(false);
    setEditingSponsorId(null);
    setSponsorForm(newSponsorInitialState);
    setSponsorModal(true);
  };

  const handleOpenEditModal = (sponsor) => {
    setIsEditMode(true);
    setEditingSponsorId(sponsor.id);
    setSponsorForm({
      sponser_name: sponsor.sponser_name || "",
      email: sponsor.email || "",
      company_name: sponsor.company_name || "",
      website: sponsor.website || "",
      address: sponsor.address || "",
      latitude: sponsor.latitude || "",
      longitude: sponsor.longitude || "",
      description: sponsor.description || "",
      image: sponsor.image || "",
    });
    setSponsorModal(true);
  };

  const handleCloseSponsorModal = () => {
    setSponsorModal(false);
    setIsEditMode(false);
    setEditingSponsorId(null);
    setSponsorForm(newSponsorInitialState);
    setCoverImage(null);
    setErrors({});
  };

  const handleSponsorFormChange = (e) => {
    const { name, value } = e.target;
    setSponsorForm({ ...sponsorForm, [name]: value });
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const maxSizeInBytes = 15 * 1024 * 1024; // 15MB

    if (file.size <= maxSizeInBytes) {
      setCoverImage(URL.createObjectURL(file));
      setCropModal("eventSponsor");
    } else {
      enqueueSnackbar("Selected cover image exceeds the 15MB size limit.", { variant: "error" });
    }
  };

  const saveLocation = (address, location) => {
    setSponsorForm({
      ...sponsorForm,
      address: address,
      latitude: location.latitude,
      longitude: location.longitude,
    });
  };

  const validateSponsorForm = () => {
    const newErrors = validator(sponsorForm, NewSponsorSchema);
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return false;
    if (!sponsorForm.image) {
      enqueueSnackbar("Please attach a Cover Image", { variant: "error" });
      return false;
    }
    return true;
  };

  const saveSponsor = async () => {
    let imageUrl = sponsorForm.image;
    let newStatus = true;

    // Only upload if it's a new file (not a URL string)
    if (sponsorForm.image && typeof sponsorForm.image !== "string") {
      const uploadResult = await awsFileUpload(
        sponsorForm.image,
        uploadImageType.sponsorProfile
      );
      if (uploadResult.status === true) {
        imageUrl = uploadResult.location;
      } else {
        enqueueSnackbar("Error uploading image", { variant: "error" });
        newStatus = false;
      }
    }

    if (newStatus) {
      // Only send fields that exist in the database - exclude 'label' and other display-only fields
      const payload = {
        sponser_name: sponsorForm.sponser_name,
        email: sponsorForm.email,
        company_name: sponsorForm.company_name,
        website: sponsorForm.website,
        address: sponsorForm.address,
        latitude: sponsorForm.latitude,
        longitude: sponsorForm.longitude,
        description: sponsorForm.description,
        image: imageUrl,
      };

      // If editing, include the ID
      if (isEditMode && editingSponsorId) {
        payload.id = editingSponsorId;
      }

      const { status, message, data } = await post(ADMIN_SPONSOR_ADD_OR_UPDATE, payload);
      if (status === true) {
        const successMessage = isEditMode 
          ? "Sponsor updated successfully!" 
          : "Sponsor created successfully!";
        enqueueSnackbar(successMessage, { variant: "success" });
        
        // Invalidate sponsors query to refresh the list
        queryClient.invalidateQueries(queryNames.SPONSOR);
        
        // Call appropriate callback
        if (isEditMode && onSponsorUpdated && data) {
          onSponsorUpdated(data);
        } else if (!isEditMode && onSponsorCreated && data) {
          onSponsorCreated(data);
        }
        
        handleCloseSponsorModal();
      } else {
        enqueueSnackbar(message || "Failed to save sponsor", { variant: "error" });
      }
    }
  };

  const sponsorMutation = useMutation(saveSponsor);

  const handleSponsorSubmit = (e) => {
    e.preventDefault();
    if (!validateSponsorForm()) return;
    sponsorMutation.mutate();
  };

  const handleCoverMouseEnter = () => setIsCoverHovered(true);
  const handleCoverMouseLeave = () => setIsCoverHovered(false);


  const handleSkip = () => {
    setCurrentStep(currentStep + 1);
  };


  const handleSaveEventMembers = async () => {
    const eventPayload = _.cloneDeep(eventForm.members);

    const sponsorsPayLoad = {event_id: eventPayload.event_id, event_step: 2, selectedSponsors};

    const { status, message, data } = await post(
      ADMIN_EVENT_CREATE,
      sponsorsPayLoad
    );
    if (status === true) {
      const newSponsors = data?.sponsors?.map((sponsor) => ({
        id: sponsor?.company_sponsor?.id,
        label: sponsor?.company_sponsor?.company_name,
      }));
      setEventForm({
        ...eventForm,
        members: {
          ...eventForm.members,
          sponsors: newSponsors,
        },
      });

      // enqueueSnackbar(message, { variant: "success" });
      setCurrentStep(currentStep + 1);
    } else enqueueSnackbar(message, { variant: "error" });
  };

  const mutation = useMutation(handleSaveEventMembers);

	const handleApiSubmit = (e) => {
    e.preventDefault();
		mutation.mutate();
	};

  return (
    <>
      {/* <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          sx={{ ...styles.continueBtn, width: 200, mb: 1 }}
          onClick={() => setModal(true)}
        >
          Add Guest Speaker
        </Button>
      </Stack> */}
      <Stack direction="row" sx={[styles.title, { mt: 3 }]}>
				<Typography component="h1">Add Sponsor</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button 
            variant="outlined" 
            onClick={handleOpenCreateModal}
            sx={{ mr: 1 }}
          >
            Create New Sponsor
          </Button>
          <Button variant="contained" onClick={handleSkip} sx={{ mr: 1 }}>
            Skip
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleApiSubmit}
            isLoading={mutation.isLoading}
          >
            Continue
          </LoadingButton>
        </Box>
			</Stack>


      <Box mt={3}>
        {sponsors.length > 0 ? (
          sponsors
            // .filter((member) => member.type !== "not_exist")
            .map((member, index) => (
              <Stack direction="row" sx={styles.memberStack} key={index}>
                <Avatar sx={styles.memberAvatar} src={member.image}>
                  {member.company_name?.charAt(0)}
                </Avatar>
                <Stack sx={styles.memberNameStack} gap="5px">
                  <Typography component="h4">{member.company_name}</Typography>
                  <Typography component="label">
                    {member.email}
                  </Typography>
                </Stack>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip title="Edit Sponsor" arrow>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenEditModal(member)}
                      sx={{ 
                        color: "#2B2A69",
                        "&:hover": { bgcolor: "rgba(43, 42, 105, 0.1)" }
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Checkbox
                    icon={
                      <RadioButtonUncheckedIcon
                        sx={{ "& path": { color: "#E2E3E4" } }}
                      />
                    }
                    checkedIcon={
                      <CheckCircleIcon sx={{ "& path": { color: "#03989E" } }} />
                    }
                    sx={styles.memberCheckbox}
                    size="medium"
                    checked={
                      !!selectedSponsors.includes( member.id )   ||
                      false
                    }
                    onChange={(e) =>
                      handleCheckboxChange(member, e.target.checked)
                    }
                  />
                </Box>
              </Stack>
            ))
        ) : (
          <Box textAlign="center">
            {loading ? (
              <CircularProgress sx={{ textAlign: "center" }} />
            ) : (
              <Typography>No Sponsors available.</Typography>
            )}
          </Box>
        )}
      </Box>

      {/* <Box>
        <Divider sx={styles.divider} />
        <FormLabel label="Guest Speakers" />
      </Box> */}

      <Fab
        sx={{
          ...styles.scrollButtonStyles,
          ...(isVisible && styles.visibleScrollButtonStyles),
        }}
        onClick={scrollToTop}
      >
        <KeyboardArrowUpIcon />
      </Fab>

      {/* Create/Edit Sponsor Drawer */}
      <Drawer anchor="right" open={sponsorModal} onClose={handleCloseSponsorModal}>
        <Box sx={drawerStyles.container}>
          <Close 
            sx={{ position: "absolute", top: 10, right: 10, cursor: "pointer" }} 
            onClick={handleCloseSponsorModal} 
          />
          <Typography variant="h2">
            {isEditMode ? "Edit Sponsor" : "Create New Sponsor"}
          </Typography>
          <Grid container spacing={3} mb={2}>
            <Grid item md={6} sm={12}>
              <FormInput
                label="Company Name"
                placeholder="Company"
                value={sponsorForm.company_name}
                name="company_name"
                onChange={handleSponsorFormChange}
                error={errors?.company_name}
                helperText={errors?.company_name || ""}
              />
            </Grid>

            <Grid item md={6} sm={12}>
              <FormInput
                label="Sponsor Name"
                placeholder="Sponsor name"
                value={sponsorForm.sponser_name}
                name="sponser_name"
                onChange={handleSponsorFormChange}
                error={errors?.sponser_name}
                helperText={errors?.sponser_name || ""}
              />
            </Grid>

            <Grid item md={6} sm={12}>
              <FormInput
                label="Email"
                placeholder="Email"
                value={sponsorForm.email}
                name="email"
                type="email"
                onChange={handleSponsorFormChange}
                error={errors?.email}
                helperText={errors?.email || ""}
              />
            </Grid>

            <Grid item md={6} sm={12}>
              <FormInput
                label="Website"
                placeholder="Website"
                value={sponsorForm.website}
                name="website"
                type="url"
                onChange={handleSponsorFormChange}
                error={errors?.website}
                helperText={errors?.website || ""}
              />
            </Grid>

            <Grid item md={12} sm={12}>
              <FormInput
                label="Description"
                placeholder="Description"
                value={sponsorForm.description}
                name="description"
                onChange={handleSponsorFormChange}
                multiline
                rows={4}
                error={errors?.description}
                helperText={errors?.description || ""}
              />
            </Grid>

            <Grid item md={12} sm={12}>
              <FormInput
                label="Address"
                placeholder="Address"
                value={sponsorForm.address}
                name="address"
                onClick={() => setAddressModal(true)}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& input": {
                    cursor: "pointer",
                    bgcolor: "rgba(226, 227, 228, 0.2)",
                    height: 40,
                    borderRadius: "4px",
                  },
                }}
                error={errors?.address}
                helperText={errors?.address || ""}
              />
            </Grid>
          </Grid>

          <FormLabel label="Cover Image" />
          {!sponsorForm.image ? (
            <Box sx={drawerStyles.eventImageContainer}>
              <Typography component="label">+Add Cover Image</Typography>
              <Button
                variant="outlined"
                onClick={() => coverRef.current.click()}
              >
                Browse Image
              </Button>
              <input
                type="file"
                hidden
                onChange={handleCoverChange}
                ref={coverRef}
                accept="image/*"
              />
            </Box>
          ) : (
            <Box
              position="relative"
              onMouseEnter={handleCoverMouseEnter}
              onMouseLeave={handleCoverMouseLeave}
            >
              <Box
                component="img"
                src={
                  typeof sponsorForm.image === "string"
                    ? sponsorForm.image
                    : URL.createObjectURL(sponsorForm.image)
                }
                sx={{
                  ...drawerStyles.coverImageContainer,
                  opacity: isCoverHovered ? 0.3 : 1,
                  transition: "opacity 0.3s ease",
                }}
                loading="lazy"
              />
              <Stack
                sx={{
                  ...drawerStyles.coverHover,
                  display: isCoverHovered ? "flex" : "none",
                  opacity: isCoverHovered ? 1 : 0.5,
                }}
              >
                <Typography component="label">+Change Cover Image</Typography>
                <Button
                  variant="outlined"
                  onClick={() => coverRef.current.click()}
                >
                  Browse Image
                </Button>
              </Stack>
            </Box>
          )}
          <input
            type="file"
            hidden
            onChange={handleCoverChange}
            ref={coverRef}
            accept="image/*"
          />

          <LoadingButton
            variant="contained"
            onClick={handleSponsorSubmit}
            isLoading={sponsorMutation.isLoading}
            sx={drawerStyles.button}
          >
            {isEditMode ? "Update Sponsor" : "Create Sponsor"}
          </LoadingButton>
        </Box>

        <CropImage
          modal={cropModal}
          setModal={setCropModal}
          image={coverImage}
          user={sponsorForm}
          setUser={setSponsorForm}
          ratio={16 / 9}
        />

        <GetAddressModal
          modal={addressModal}
          setModal={setAddressModal}
          saveLocation={saveLocation}
        />
      </Drawer>
    </>
  );
};

export default SponsorTab;
